import type { Store } from '@blocksuite/affine/store';

import type { MindmapNodeType } from '../types';

/**
 * URL schemes for the two ends of the bidirectional card↔source link.
 * The `CanvasLinkRouter` (mounted at workspace-layout level) intercepts
 * clicks on `<a>` elements whose `href` starts with either prefix and
 * routes them to the right navigation handler.
 */
export const CARD_LINK_SCHEME = 'curiescious-card://';
export const SOURCE_LINK_SCHEME = 'curiescious-source://';

export function cardHref(cardNoteId: string): string {
  return `${CARD_LINK_SCHEME}${encodeURIComponent(cardNoteId)}`;
}

export function sourceHref(docId: string, blockId: string): string {
  return `${SOURCE_LINK_SCHEME}${encodeURIComponent(docId)}/${encodeURIComponent(blockId)}`;
}

export function parseCardHref(href: string): string | null {
  if (!href.startsWith(CARD_LINK_SCHEME)) return null;
  try {
    return decodeURIComponent(href.slice(CARD_LINK_SCHEME.length));
  } catch {
    return null;
  }
}

export function parseSourceHref(
  href: string
): { docId: string; blockId: string } | null {
  if (!href.startsWith(SOURCE_LINK_SCHEME)) return null;
  const rest = href.slice(SOURCE_LINK_SCHEME.length);
  const sep = rest.indexOf('/');
  if (sep <= 0) return null;
  try {
    return {
      docId: decodeURIComponent(rest.slice(0, sep)),
      blockId: decodeURIComponent(rest.slice(sep + 1)),
    };
  } catch {
    return null;
  }
}

/**
 * Per-type alpha-tinted backgrounds for the source highlight. Each
 * matches the corresponding `strokeColor` in `NODE_TYPE_REGISTRY` but
 * dropped to low opacity so the inline highlight doesn't fight the
 * doc's typography. Researchers should be able to scan the doc and
 * see at a glance which colors mean what.
 */
export const SOURCE_BACKGROUNDS: Record<MindmapNodeType, string> = {
  finding: 'rgba(0, 43, 87, 0.25)',
  hypothesis: 'rgba(180, 83, 9, 0.28)',
  question: 'rgba(109, 40, 217, 0.28)',
  method: 'rgba(21, 128, 61, 0.28)',
  quote: 'rgba(71, 85, 105, 0.22)',
};

/**
 * Bright flash colour for the temporary "you-landed-here" pulse the
 * router applies after scrolling the source block into view. Cleared
 * after `FLASH_DURATION_MS`. Yellow because that's the universal
 * "look here" colour and is theme-neutral.
 */
export const FLASH_BACKGROUND = 'rgba(255, 236, 130, 0.6)';
export const FLASH_DURATION_MS = 2000;

type FormatTarget = {
  text?: {
    format: (
      index: number,
      length: number,
      attrs: Record<string, unknown>
    ) => void;
    toString: () => string;
  } | null;
};

/**
 * Apply the source-side highlight: combine the type-colored
 * `background` with a `link` carrying the card's id so the same
 * formatted range serves both as visual marker AND as the click
 * target.  Uses `text.format` directly on the block model — this is
 * the BlockSuite API used everywhere for inline-format toggles.
 */
export function applySourceLink(
  doc: Store,
  sourceBlockId: string,
  range: { index: number; length: number },
  cardNoteId: string,
  nodeType: MindmapNodeType
): void {
  const target = doc.getBlock(sourceBlockId)?.model as FormatTarget | undefined;
  if (!target?.text) {
    console.log(
      '[source-link] block has no text — skipping highlight',
      sourceBlockId
    );
    return;
  }
  if (range.length <= 0) {
    console.log('[source-link] empty range — skipping highlight');
    return;
  }
  try {
    target.text.format(range.index, range.length, {
      background: SOURCE_BACKGROUNDS[nodeType],
      link: cardHref(cardNoteId),
    });
    console.log('[source-link] highlight applied', {
      sourceBlockId,
      range,
      nodeType,
      cardNoteId,
    });
  } catch (err) {
    console.warn('[source-link] format failed', err);
  }
}

/**
 * Clear the source-side highlight + link. Used by undo. We pass null
 * for each attribute we want to remove, matching BlockSuite's format
 * semantics (null clears).
 */
export function removeSourceLink(
  doc: Store,
  sourceBlockId: string,
  range: { index: number; length: number }
): void {
  const target = doc.getBlock(sourceBlockId)?.model as FormatTarget | undefined;
  if (!target?.text) return;
  if (range.length <= 0) return;
  try {
    target.text.format(range.index, range.length, {
      background: null,
      link: null,
    });
  } catch {
    /* range gone — nothing to clear */
  }
}

/**
 * Temporary flash highlight for the "you-landed-here" pulse. Yellow
 * background applied for `FLASH_DURATION_MS`, then cleared. The first
 * argument is the block id; we format the whole block's text length.
 */
export function flashBlock(doc: Store, blockId: string): void {
  const target = doc.getBlock(blockId)?.model as FormatTarget | undefined;
  if (!target?.text) return;
  const len = target.text.toString().length;
  if (len === 0) return;
  try {
    target.text.format(0, len, { background: FLASH_BACKGROUND });
  } catch {
    return;
  }
  setTimeout(() => {
    try {
      const stillThere = doc.getBlock(blockId)?.model as
        | FormatTarget
        | undefined;
      const curLen = stillThere?.text?.toString().length ?? 0;
      if (curLen > 0) {
        stillThere?.text?.format(0, curLen, { background: null });
      }
    } catch {
      /* block deleted while flashing — ignore */
    }
  }, FLASH_DURATION_MS);
}
